import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { Certificate } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';

function courseTitle(certificate: Certificate) {
  return certificate.course_details?.title || `Course #${certificate.course}`;
}

function instructorName(certificate: Certificate) {
  const details = certificate.course_details;
  const named = (details?.tutor_name || '').trim();
  if (named) return named;
  const tutor = details?.tutor;
  const fromFields = [tutor?.first_name, tutor?.last_name].filter(Boolean).join(' ').trim();
  if (fromFields) return fromFields;
  const username = (tutor?.username || '').trim();
  if (username.includes('@')) return username.split('@')[0];
  return username || 'Phino LMS';
}

function kindLabel(certificate: Certificate) {
  return certificate.kind === 'merit' ? 'Certificate of Merit' : 'Certificate of Completion';
}

function shortId(id?: string) {
  if (!id) return '';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export default function CertificateList() {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getCertificates();

      if (response.success && response.data) {
        const data = response.data as Certificate[] | { results?: Certificate[] };
        setCertificates(Array.isArray(data) ? data : data.results || []);
      } else {
        setError(response.error || 'Failed to load certificates');
      }
    } catch {
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = (certificate: Certificate) => {
    if (certificate.pdf_file) {
      const link = document.createElement('a');
      link.href = certificate.pdf_file;
      link.download = `${courseTitle(certificate)}_Certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Certificate PDF is not available for download.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={loadCertificates} className="mt-2 text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">My Certificates</h2>
        <p className="text-sm text-slate-500">
          {certificates.length} certificate{certificates.length !== 1 ? 's' : ''} earned
        </p>
      </div>

      {certificates.length === 0 ? (
        <EmptyState
          icon="🎖️"
          title="No certificates earned yet."
          description="Finish the course requirements. If the course has an exam or assignments, you must also meet the pass mark."
          hintsTitle="How to earn a certificate:"
          hints={[
            { icon: '📚', label: 'Enroll in a course' },
            { icon: '✅', label: 'Complete all lessons' },
            { icon: '📝', label: 'Pass exams or graded assignments if the course has them' },
          ]}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {certificates.map((certificate) => {
            const merit = certificate.kind === 'merit';
            const frame = merit
              ? 'border-[#C4A35A] bg-[#FBF8F1]'
              : 'border-[#1B365D] bg-[#FBF8F1]';
            const inner = merit ? 'border-[#1B365D]/40' : 'border-[#C4A35A]/70';
            const accent = merit ? 'text-[#C4A35A]' : 'text-[#1B365D]';
            return (
              <div key={certificate.id} className={`rounded-sm border-4 ${frame} shadow-md`}>
                <div className={`m-1.5 rounded-sm border ${inner} px-6 py-5`}>
                  <p className={`text-center text-[10px] font-semibold tracking-[0.28em] ${accent}`}>
                    PHINO LMS
                  </p>
                  <h3 className="mt-2 text-center font-serif text-xl font-semibold text-[#1B365D]">
                    {kindLabel(certificate)}
                  </h3>
                  <p className="mt-3 text-center font-serif text-lg text-slate-900 leading-snug">
                    {courseTitle(certificate)}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
                    <span>Instructor: {instructorName(certificate)}</span>
                    <span aria-hidden>·</span>
                    <span>
                      Issued{' '}
                      {new Date(certificate.issued_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    {certificate.score != null && (
                      <>
                        <span aria-hidden>·</span>
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${merit ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
                          {Number(certificate.score).toFixed(Number(certificate.score) % 1 === 0 ? 0 : 1)}%
                        </span>
                      </>
                    )}
                  </div>
                  {certificate.certificate_id && (
                    <p className="mt-2 text-center font-mono text-[10px] text-slate-400" title={certificate.certificate_id}>
                      ID {shortId(certificate.certificate_id)}
                    </p>
                  )}
                  <div className="mt-5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleDownloadCertificate(certificate)}
                      disabled={!certificate.pdf_file}
                      className="flex-1 rounded-md bg-[#1B365D] px-3 py-2 text-sm font-semibold text-white hover:bg-[#152a4a] disabled:opacity-50"
                    >
                      {certificate.pdf_file ? 'Download PDF' : 'PDF unavailable'}
                    </button>
                    {certificate.pdf_file && (
                      <a
                        href={certificate.pdf_file}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Open
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
        <h3 className="font-medium text-slate-900 mb-2">About certificates</h3>
        <ul className="space-y-1 list-disc list-inside">
          <li>Completion is awarded when you finish required lessons and pass any assessments.</li>
          <li>Merit is awarded for a high overall score.</li>
          <li>Each certificate has a unique ID. Download or open the PDF to share it.</li>
        </ul>
      </div>
    </div>
  );
}
